import { css, html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import { SignalElement, inject } from "ui-common/di";
import type { IAuthState, IAuthService } from "ui-common/services/auth/types";
import { rpc } from "ui-common/services/rpc";
import "ui-components/button/wow-button.ts";
import "ui-components/form-elements/input/wow-input.ts";

import { NotificationService } from "ui-components/notification";
import "../components/wow-logo-full/wow-logo-full";
import { navigateTo } from "../router";
import { t, translate } from "../translations/i18n";
import { queryClient } from "ui-common/data-query-controller";
import { profileDataQueryKey } from "../queries/profile-data";
import { VerifyEmailErrorMessages } from "api-contracts/rpc/verify-email";
import { isValidPassword } from "helpers/password";
import { WowFormController } from "ui-common";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
    box-sizing: border-box;
  }

  * {
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    box-sizing: border-box;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .content {
    background-color: var(--sds-color-background-neutral-strong);
    padding: var(--sds-size-space-800) var(--sds-size-space-500);
    border-radius: var(--sds-size-border-radius-200);
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    text-align: center;
    align-items: center;
    box-sizing: border-box;
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
    color: var(--sds-color-text-default-inverse);
    margin: 0;
    width: 100%;
  }

  .message {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-03);
    line-height: 1.5;
    color: var(--sds-color-text-default-inverse);
    margin: 0;
    width: 100%;
  }

  .error-message {
    color: var(--sds-color-text-danger-default);
  }

  .success-button {
    width: 100%;
    margin-top: var(--sds-size-space-400);
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--sds-color-background-neutral-secondary);
    border-top: 3px solid var(--sds-color-text-default-inverse);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .password-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-600);
  }

  .form-error {
    color: var(--sds-color-text-danger-default);
    font-size: var(--sds-typography-scale-02);
    text-align: center;
  }

  .subtitle {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-02);
    line-height: 1.5;
    color: var(--sds-color-text-default-secondary);
    margin: 0;
  }

  form {
    display: contents;
  }
`;

type VerificationState = "ready" | "loading" | "success" | "password-setup" | "password-success" | "error";

interface PasswordFormData {
  password: string;
  confirmPassword: string;
}

@customElement("email-confirmation-page")
export class EmailConfirmationPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<IAuthService>("authService")
  private authService!: IAuthService;

  static override styles = [styles];

  @state()
  private verificationState: VerificationState = "ready";

  @state()
  private errorMessage = "";

  @state()
  private purchaseEventHandle = "";

  @state()
  private purchaseEventId = "";

  @state()
  private isSettingPassword = false;

  @state()
  private passwordFormError = "";

  private passwordFormController: WowFormController<PasswordFormData> = new WowFormController(
    this,
    {
      password: "",
      confirmPassword: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validators: {
        password: [
          (value: string) => {
            if (!value) return translate("reset-password.passwordRequired");

            const { minLength, maxLength, hasNumber, hasUpperLowerCase } = isValidPassword(value);

            if (!minLength) return translate("reset-password.passwordMinLength");
            if (!maxLength) return translate("reset-password.passwordMaxLength");
            if (!hasNumber) return translate("reset-password.passwordNumber");
            if (!hasUpperLowerCase) return translate("reset-password.passwordCase");

            return;
          },
        ],
        confirmPassword: [
          (value: string) => {
            const password = this.passwordFormController.getValue("password") as string;

            if (!value) {
              return translate("reset-password.passwordRequired");
            }
            // eslint-disable-next-line security/detect-possible-timing-attacks
            if (value !== password) {
              return translate("reset-password.passwordMismatch");
            }
            return;
          },
        ],
      },
    },
    async (event) => {
      if (!event || !event.data || !event.isValid) {
        return;
      }

      this.passwordFormError = "";

      const { data, isValid } = event;

      if (!isValid) {
        this.passwordFormError = translate("reset-password.fixErrors");
        return;
      }

      if (data.password !== data.confirmPassword) {
        this.passwordFormError = translate("reset-password.passwordMismatch");
        return;
      }

      this.isSettingPassword = true;

      try {
        await rpc("changePassword", {
          password: data.password,
          confirmPassword: data.confirmPassword,
          onboarding: true,
        });

        this.verificationState = "password-success";
      } catch (error) {
        this.passwordFormError = translate("reset-password.generalError");
        console.error("Password change error:", error);
      } finally {
        this.isSettingPassword = false;
      }
    },
  );

  constructor() {
    super();
  }

  private handleVerifyClick() {
    this.verificationState = "loading";
    this.verifyEmail();
  }

  private async verifyEmail() {
    try {
      console.log("[email-confirmation] verifyEmail called, URL:", window.location.href);
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");
      const email = urlParams.get("email");
      const mode = urlParams.get("mode");
      const id = urlParams.get("id");
      const onboarding = urlParams.get("onboarding") === "true";
      const purchaseEventHandle = urlParams.get("purchaseEventHandle");
      const purchaseEventId = urlParams.get("purchaseEventId");

      console.log("[email-confirmation] URL params:", { token, email, mode, id, purchaseEventHandle, purchaseEventId });

      if (!token || !email) {
        this.verificationState = "error";
        this.errorMessage = "Missing verification parameters";
        return;
      }

      const decodedEmail = decodeURIComponent(email);
      const decodedToken = decodeURIComponent(token);

      if (mode === "change" && id) {
        return this.verifyChangeEmail(id, decodedEmail, decodedToken);
      }

      await new Promise((resolve) => setTimeout(resolve, 3000));

      const { accessToken } = await rpc("verifyEmail", {
        email: decodedEmail,
        token: decodedToken,
        purchaseEventHandle: purchaseEventHandle || undefined,
        purchaseEventId: purchaseEventId || undefined,
      });
      this.authService.signIn(accessToken);

      if (purchaseEventHandle && purchaseEventId) {
        this.purchaseEventHandle = purchaseEventHandle;
        this.purchaseEventId = purchaseEventId;
        this.verificationState = "success";
        return;
      }

      if (onboarding) {
        window.open(`/my/change-password?onboarding=true`, "_self", "noopener,noreferrer");
      }

      this.verificationState = "success";
    } catch (error) {
      console.error("Verification error:", error);
      this.verificationState = "error";
      this.errorMessage = error instanceof Error ? error.message : "Verification failed";
    }
  }

  private async verifyChangeEmail(userId: string, email: string, token: string) {
    if (!(email && token)) return;

    await rpc("changeEmail", { userId, email, token });
    await queryClient.invalidateQueries({ queryKey: [profileDataQueryKey] });
    NotificationService.success(`Your email has been successfully updated to ${email}`);
    if (this.authState.currentUser.get()?.handle) return navigateTo("profile");
    navigateTo("sign-in");
  }

  private handleContinue() {
    // Only allow redirect if verification was successful
    if (this.verificationState === "success") {
      this.redirectPostVerification();
    }
  }

  private redirectPostVerification() {
    if (this.purchaseEventHandle && this.purchaseEventId) {
      window.location.href = `/live-events/${this.purchaseEventHandle}/${this.purchaseEventId}`;
    } else {
      window.location.href = "/feed";
    }
  }

  private renderErrorContent() {
    if (this.errorMessage === VerifyEmailErrorMessages.InvalidToken) {
      return html`
        <h1 class="title">${t("emailConfirmation.error")}</h1>
        <p class="message error-message">${t("emailConfirmation.errorMessageInvalidToken")}</p>
      `;
    }

    if (this.errorMessage === VerifyEmailErrorMessages.AlreadyVerified) {
      return html`
        <h1 class="title">${t("emailConfirmation.alreadyVerified")}</h1>
        <p class="message error-message">${t("emailConfirmation.alreadyVerifiedMessage")}</p>
        <wow-button class="success-button" variant="primary" size="medium" @click=${this.handleContinue}>
          ${t("emailConfirmation.continue")}
        </wow-button>
      `;
    }

    return html`
      <h1 class="title">${t("emailConfirmation.error")}</h1>
      <p class="message error-message">${t("emailConfirmation.errorMessage")}</p>
    `;
  }

  private handleContinueToEvent() {
    if (this.purchaseEventHandle && this.purchaseEventId) {
      window.location.href = `/live-events/${this.purchaseEventHandle}/${this.purchaseEventId}`;
    }
  }

  private renderPasswordSetup() {
    return html`
      <h1 class="title">${t("emailConfirmation.setPasswordTitle")}</h1>
      <p class="subtitle">${t("emailConfirmation.setPasswordSubtitle")}</p>

      <form id="password-form">
        <div class="password-form">
          <wow-input
            name=${this.passwordFormController.fieldNames.password}
            type="password"
            placeholder=${t("emailConfirmation.newPassword")}
            autocomplete="new-password"
            required
            ?disabled=${this.isSettingPassword}
          >
          </wow-input>

          <wow-input
            name=${this.passwordFormController.fieldNames.confirmPassword}
            type="password"
            placeholder=${t("emailConfirmation.confirmPassword")}
            autocomplete="new-password"
            required
            ?disabled=${this.isSettingPassword}
          >
          </wow-input>

          ${this.passwordFormError ? html`<div class="form-error">${this.passwordFormError}</div>` : nothing}

          <wow-button form="password-form" class="success-button" type="submit" ?disabled=${this.isSettingPassword}>
            ${this.isSettingPassword
              ? t("emailConfirmation.settingPassword")
              : t("emailConfirmation.setPasswordButton")}
          </wow-button>
        </div>
      </form>
    `;
  }

  private renderPasswordSuccess() {
    return html`
      <h1 class="title">${t("emailConfirmation.passwordSetSuccess")}</h1>
      <p class="message">${t("emailConfirmation.accessingEvent")}</p>
      <wow-button class="success-button" variant="primary" size="medium" @click=${this.handleContinueToEvent}>
        ${t("emailConfirmation.continueToEvent")}
      </wow-button>
    `;
  }

  private renderContent() {
    switch (this.verificationState) {
      case "ready":
        return html`
          <h1 class="title">${t("emailConfirmation.readyToVerify")}</h1>
          <p class="message">${t("emailConfirmation.clickToVerify")}</p>
          <wow-button class="success-button" variant="primary" size="medium" @click=${this.handleVerifyClick}>
            ${t("emailConfirmation.verifyButton")}
          </wow-button>
        `;

      case "loading":
        return html`
          <div class="loading-spinner"></div>
          <h1 class="title">${t("emailConfirmation.verifying")}</h1>
          <p class="message">${t("emailConfirmation.pleaseWait")}</p>
        `;

      case "success":
        return html`
          <h1 class="title">${t("emailConfirmation.success")}</h1>
          <p class="message">${t("emailConfirmation.successMessage")}</p>
          <wow-button class="success-button" variant="primary" size="medium" @click=${this.handleContinue}>
            ${t("emailConfirmation.continue")}
          </wow-button>
        `;

      case "password-setup":
        return this.renderPasswordSetup();

      case "password-success":
        return this.renderPasswordSuccess();

      case "error":
        return this.renderErrorContent();

      default:
        return html`<p class="message error-message">Unknown error</p>`;
    }
  }

  protected override render() {
    return html`
      <div class="container">
        <div class="logo">
          <wow-logo-full></wow-logo-full>
        </div>

        <div class="content">${this.renderContent()}</div>
      </div>
    `;
  }
}
